import { apiFetch, authenticatedFetch, getToken, getOrCreateDeviceId, getClientPlatform } from '../Global/authService';

function addDeviceInfo<T extends Record<string, any>>(data: T): T & { deviceId: string; platform: string } {
  return {
    ...data,
    deviceId: data.deviceId || getOrCreateDeviceId(),
    platform: data.platform || getClientPlatform(),
  };
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TraineeProfile {
  id?: number;
  username: string;
  name?: string;
  supervisorId?: number;
  managementLevel?: 'NONE' | 'SUPERVISOR' | 'MANAGER' | string;
  isApproved: boolean;
  leaveBalance: number;
  totalLeaves: number;
  permissions?: string;
  editAccessGrantedUntil?: string;
  editRequestPending?: boolean;
  editRequestReason?: string;
  editRequestRequestedAt?: string;
  editRequestReviewedAt?: string;
  editRequestReviewedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  photoUrl?: string;
  officeTimings?: string;
  dateOfJoining?: string;
  aadhaarNumber?: string;
  aadhaarPhotoUrl?: string;
  panNumber?: string;
  panPhotoUrl?: string;
  bankName?: string;
  bankAccountNo?: string;
  bankIfscCode?: string;
  bankBranchName?: string;
  emergencyContactName?: string;
  emergencyContactMobile?: string;
  fatherName?: string;
  motherName?: string;
  presentAddress?: string;
  permanentAddress?: string;
  educationCompleted?: string;
  subClassification?: string;
}

export interface AttendanceRecord {
  id?: number;
  username: string;
  date: string;
  inTime?: string;
  outTime?: string;
  status: string;
  isLate: boolean;
  info?: string;
  absenceCategory?: string;
  absenceRemark?: string;
  absenceMarkedBy?: string;
  absenceMarkedAt?: string;
  inTime1?: string; outTime1?: string; inBranch1?: string; outBranch1?: string;
  inTime2?: string; outTime2?: string; inBranch2?: string; outBranch2?: string;
  inTime3?: string; outTime3?: string; inBranch3?: string; outBranch3?: string;
  inTime4?: string; outTime4?: string;
  inTime5?: string; outTime5?: string;
}

export interface AttendanceSlot {
  id?: number;
  username: string;
  dayOfWeek: string;
  slotNo: number;
  startTime: string;
  endTime: string;
}

export interface LeaveRequest {
  id?: number;
  username: string;
  name?: string;
  branch?: string;
  startDate: string;
  endDate: string;
  reason?: string;
  halfDay?: boolean;
  leaveSession?: 'FIRST_HALF' | 'SECOND_HALF' | string;
  adminReason?: string;
  status: string;
  appliedDate?: string;
  createdAt?: string;
}

export interface EarlyLeaveRequest {
  id?: number;
  username: string;
  branch?: string;
  date: string;
  slotNo: number;
  minutesEarly: number;
  reason?: string;
  remarks?: string;
  adminReason?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BreakLog {
  id?: number;
  username: string;
  date: string;
  breakOut: string;
  breakIn?: string;
  reason?: string;
  status?: string;
  bookletNo?: string;
  collegeName?: string;
  subject?: string;
  topicsCovered?: string;
  conveyance?: string;
  numberOfHours?: string;
  fromTime?: string;
  toTime?: string;
  breakOutLat?: number | null;
  breakOutLng?: number | null;
  breakInLat?: number | null;
  breakInLng?: number | null;
  breakOutLocationName?: string | null;
  breakInLocationName?: string | null;
  createdAt?: string;
}

export interface AdminBreakUpdatePayload {
  username?: string;
  date?: string;
  breakOut?: string;
  breakIn?: string;
  reason?: string;
  status?: string;
  bookletNo?: string;
  collegeName?: string;
  subject?: string;
  topicsCovered?: string;
  conveyance?: string;
  numberOfHours?: string;
  fromTime?: string;
  toTime?: string;
  breakOutLat?: number | null;
  breakOutLng?: number | null;
  breakInLat?: number | null;
  breakInLng?: number | null;
}

export interface BreakRequestPayload extends Partial<BreakLog> {
  lat: number;
  lng: number;
}

export interface Memo {
  id?: number;
  senderUsername: string;
  senderName?: string;
  recipientUsername: string;
  recipientName?: string;
  memoType?: string;
  subject?: string;
  message: string;
  isRead: boolean;
  read?: boolean;
  readAt?: string;
  createdAt?: string;
}

export interface MemoRecipient {
  username: string;
  name?: string;
  branch?: string;
}

export interface ExtraClassLog {
  id?: number;
  username: string;
  date: string;
  day?: string;
  subject: string;
  batchNo: string;
  classType?: 'EXTRA_CLASS' | 'OTHER_CENTER';
  classMode?: 'ONLINE' | 'OFFLINE' | string;
  duration: number;
  startTime?: string;
  endTime?: string;
  noOfStudents?: number;
  centerName?: string;
  remarks?: string;
  status: string;
}

export interface ClassCancelledLog {
  id?: number;
  username: string;
  date: string;
  day?: string;
  subject: string;
  batchNo: string;
  centerName?: string;
  reason?: string;
  remarks?: string;
}

export interface AttendanceHoliday {
  id?: number;
  date: string;
  name: string;
}

export interface AttendanceDropdownOption {
  id?: number;
  type: string;
  value: string;
}

export interface AttendanceStatusResponse {
  today: AttendanceRecord | null;
  slots: AttendanceSlot[];
  date: string;
  status?: string;
  inTime?: string;
  outTime?: string;
  forgotPunchOut?: boolean;
  currentlyOnBreak?: boolean;
  breakPending?: boolean;
  activeBreak?: BreakLog | null;
  pendingBreak?: BreakLog | null;
  completedBreaks?: BreakLog[];
}

export interface ManualAttendanceRequest {
  date?: string;
  slotNo?: number | null;
  inTime?: string;
  outTime?: string;
  info?: string;
  clearPunchOut?: boolean;
  status?: string;
  absenceCategory?: string;
  absenceRemark?: string;
  clearAbsenceMarker?: boolean;
}

export interface CounsellorHierarchyMapping {
  id?: number;
  managerUsername: string;
  subordinateUsername: string;
  branch: string;
  active: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// ── Helper ────────────────────────────────────────────────────────────────────

type ResponseOptions = {
  unregisteredDeviceMessage?: string;
};

const fallbackErrorMessage = (status: number) => {
  if (status === 400) return 'Invalid request. Please check the entered details.';
  if (status === 401) return 'Your session has expired. Please log in again.';
  if (status === 403) return 'Access denied. You may be outside the geofence or using an unregistered device.';
  if (status === 404) return 'Requested resource was not found.';
  if (status === 409) return 'Conflict: the same record already exists.';
  if (status >= 500) return 'Server error. Please try again in a moment.';
  return `Request failed (${status}).`;
};

const isDeviceRestrictionError = (message: string) => {
  const normalized = String(message || '').toLowerCase();
  return normalized.includes('device') && (normalized.includes('register') || normalized.includes('verification'));
};

async function handleResponse<T>(res: Response, options?: ResponseOptions): Promise<T> {
  if (!res.ok) {
    const raw = await res.text();
    const trimmed = raw?.trim();

    if (trimmed) {
      let parsed: {
        message?: string;
        error?: string;
        details?: string;
        status?: number;
      } | null = null;

      try {
        parsed = JSON.parse(trimmed) as {
          message?: string;
          error?: string;
          details?: string;
          status?: number;
        };
      } catch {
        parsed = null;
      }

      if (parsed) {
        const parsedMessage = parsed.message || parsed.details || '';
        const parsedError = parsed.error || '';
        const normalizedParsedError = String(parsedError).trim().toLowerCase();
        const isGenericParsedError =
          normalizedParsedError === 'bad request' ||
          normalizedParsedError === 'unauthorized' ||
          normalizedParsedError === 'forbidden' ||
          normalizedParsedError === 'not found' ||
          normalizedParsedError === 'internal server error';

        if (res.status === 403 && options?.unregisteredDeviceMessage && isDeviceRestrictionError(parsedMessage || parsedError)) {
          throw new Error(options.unregisteredDeviceMessage);
        }

        const finalMessage =
          (parsedMessage && parsedMessage.trim()) ||
          (!isGenericParsedError && parsedError) ||
          fallbackErrorMessage(res.status);
        throw new Error(finalMessage);
      }

      if (res.status === 403 && options?.unregisteredDeviceMessage && isDeviceRestrictionError(trimmed || '')) {
        throw new Error(options.unregisteredDeviceMessage);
      }
      throw new Error(trimmed || fallbackErrorMessage(res.status));
    }

    throw new Error(fallbackErrorMessage(res.status));
  }
  if (res.status === 204) return undefined as T;

  const raw = await res.text();
  const trimmed = raw?.trim();
  if (!trimmed) {
    return undefined as T;
  }

  return JSON.parse(trimmed) as T;
}

const normalizeMemo = (memo: any): Memo => ({
  ...memo,
  isRead: Boolean(memo?.isRead ?? memo?.read),
});

const normalizeMemos = (memos: any[]): Memo[] => (memos || []).map(normalizeMemo);

// ── Trainee (FACULTY) APIs ────────────────────────────────────────────────────

export const getAttendanceStatus = () =>
  authenticatedFetch('/api/attendance/status').then(r => handleResponse<AttendanceStatusResponse>(r));

export const punch = (lat: number, lng: number, deviceId?: string) =>
  authenticatedFetch('/api/attendance/punch', {
    method: 'POST',
    body: JSON.stringify({ 
      lat, 
      lng, 
      deviceId: deviceId || getOrCreateDeviceId(),
      platform: getClientPlatform(),
    }),
  }).then(r => handleResponse<{ action: string; time: string; record: AttendanceRecord }>(r, {
    unregisteredDeviceMessage: 'Please punch in/out using your registered device.',
  }));

export const getAttendanceHistory = () =>
  authenticatedFetch('/api/attendance/history').then(r => handleResponse<AttendanceRecord[]>(r));

export const getLeaveStatus = async () => {
  const res = await authenticatedFetch('/api/attendance/leave/status');
  if (res.ok) {
    return handleResponse<{ leaveBalance: number; totalLeaves: number; pendingRequests: LeaveRequest[] }>(res);
  }

  if (res.status === 404) {
    // Backward compatibility for servers where attendance leave endpoints are not deployed yet.
    const profileRes = await authenticatedFetch('/api/attendance/profile');
    if (!profileRes.ok) {
      return handleResponse<{ leaveBalance: number; totalLeaves: number; pendingRequests: LeaveRequest[] }>(res);
    }
    const profile = await handleResponse<TraineeProfile>(profileRes);
    return {
      leaveBalance: profile.leaveBalance || 0,
      totalLeaves: profile.totalLeaves || 0,
      pendingRequests: [],
    };
  }

  return handleResponse<{ leaveBalance: number; totalLeaves: number; pendingRequests: LeaveRequest[] }>(res);
};

export const getLeaveHistory = () =>
  authenticatedFetch('/api/attendance/leave/history').then(r => handleResponse<LeaveRequest[]>(r));

export const applyLeave = (data: Partial<LeaveRequest>) =>
  authenticatedFetch('/api/attendance/leave/request', {
    method: 'POST',
    body: JSON.stringify(addDeviceInfo(data)),
  }).then(r => handleResponse<LeaveRequest>(r));

export const getEarlyLeaveStatus = () =>
  authenticatedFetch('/api/attendance/early-leave/status')
    .then(r => handleResponse<{ pendingRequests: EarlyLeaveRequest[] }>(r));

export const getEarlyLeaveHistory = () =>
  authenticatedFetch('/api/attendance/early-leave/history').then(r => handleResponse<EarlyLeaveRequest[]>(r));

export const getEarlyLeaveSlots = (date: string) =>
  authenticatedFetch(`/api/attendance/early-leave/slots?date=${encodeURIComponent(date)}`)
    .then(r => handleResponse<AttendanceSlot[]>(r));

export const applyEarlyLeave = (data: Partial<EarlyLeaveRequest>) =>
  authenticatedFetch('/api/attendance/early-leave/request', {
    method: 'POST',
    body: JSON.stringify(addDeviceInfo(data)),
  }).then(r => handleResponse<EarlyLeaveRequest>(r));

export const breakOut = (data: BreakRequestPayload) =>
  authenticatedFetch('/api/attendance/break/out', {
    method: 'POST',
    body: JSON.stringify(addDeviceInfo(data)),
  }).then(r => handleResponse<BreakLog>(r));

export const breakIn = (data: Pick<BreakRequestPayload, 'lat' | 'lng'>) =>
  authenticatedFetch('/api/attendance/break/in', {
    method: 'POST',
    body: JSON.stringify(addDeviceInfo(data)),
  }).then(r => handleResponse<BreakLog>(r));

export const getBreakHistory = () =>
  authenticatedFetch('/api/attendance/break/history').then(r => handleResponse<BreakLog[]>(r));

export const applyExtraClass = (data: Partial<ExtraClassLog>) =>
  authenticatedFetch('/api/attendance/extra-class/apply', {
    method: 'POST',
    body: JSON.stringify(data),
  }).then(r => handleResponse<ExtraClassLog>(r));

export const getExtraClasses = () =>
  authenticatedFetch('/api/attendance/extra-class/status').then(r => handleResponse<ExtraClassLog[]>(r));

export const applyClassCancelled = (data: Partial<ClassCancelledLog>) =>
  authenticatedFetch('/api/attendance/class-cancelled/apply', {
    method: 'POST',
    body: JSON.stringify(data),
  }).then(r => handleResponse<ClassCancelledLog>(r));

export const getClassCancelled = () =>
  authenticatedFetch('/api/attendance/class-cancelled/status').then(r => handleResponse<ClassCancelledLog[]>(r));

export const getMyProfile = () =>
  authenticatedFetch('/api/attendance/profile').then(r => handleResponse<TraineeProfile>(r));

export const updateMyProfile = (data: Partial<TraineeProfile>) =>
  authenticatedFetch('/api/attendance/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(r => handleResponse<TraineeProfile>(r));

export const requestMyProfileEdit = (reason?: string) =>
  authenticatedFetch('/api/attendance/profile/request-edit', {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }).then(r => handleResponse<TraineeProfile>(r));

export const getMonthlyJson = (year: number, month: number) =>
  authenticatedFetch(`/api/attendance/reports/monthly-json?year=${year}&month=${month}`)
    .then(r => handleResponse<{ records: AttendanceRecord[]; holidays: string[]; year: number; month: number }>(r));

export const getReceivedMemos = () =>
  authenticatedFetch('/api/attendance/memos/received').then(r => handleResponse<any[]>(r)).then(normalizeMemos);

export const markMemoRead = (id: number) =>
  authenticatedFetch(`/api/attendance/memos/${id}/read`, { method: 'POST' }).then(r => handleResponse<any>(r)).then(normalizeMemo);

// ── Admin APIs ────────────────────────────────────────────────────────────────

export const getTodayAttendance = (branch?: string) => {
  const params = new URLSearchParams();
  if (branch && branch.trim() && branch.trim().toUpperCase() !== 'ALL') {
    params.set('branch', branch.trim());
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return authenticatedFetch(`/api/admin/attendance/today${suffix}`).then(r => handleResponse<any[]>(r));
};

export const getDailyAttendance = (date: string, branch?: string) => {
  const params = new URLSearchParams({ date });
  if (branch && branch.trim() && branch.trim().toUpperCase() !== 'ALL') {
    params.append('branch', branch.trim());
  }
  return authenticatedFetch(`/api/admin/attendance/daily?${params}`).then(r => handleResponse<any[]>(r));
};

export const getPendingBreaks = () =>
  authenticatedFetch('/api/admin/attendance/breaks/pending').then(r => handleResponse<BreakLog[]>(r));

export const processBreak = (breakId: number, action: 'APPROVE' | 'REJECT' | 'REVOKE', adminReason?: string) =>
  authenticatedFetch('/api/admin/attendance/breaks/process', {
    method: 'POST',
    body: JSON.stringify({ breakId, action, adminReason }),
  }).then(r => handleResponse<BreakLog>(r));

export const downloadMonthlyReport = async (year: number, month: number, branch?: string) => {
  const token = getToken();
  const query = new URLSearchParams({ year: String(year), month: String(month) });
  if (branch) query.set('branch', branch);
  const res = await apiFetch(`/api/admin/attendance/reports/monthly?${query.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to download report');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `attendance_${year}_${month}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

export const downloadIndividualReport = async (username: string, year: number, month: number) => {
  const token = getToken();
  const res = await apiFetch(`/api/admin/attendance/reports/individual/${username}?year=${year}&month=${month}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to download report');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${username}_${year}_${month}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

export const getIndividualMonthlyJson = (username: string, year: number, month: number) =>
  authenticatedFetch(`/api/admin/attendance/reports/individual/${encodeURIComponent(username)}/monthly-json?year=${year}&month=${month}`)
    .then(r => handleResponse<{ records: AttendanceRecord[]; holidays: string[]; year: number; month: number }>(r));

export const getAllTrainees = () =>
  authenticatedFetch('/api/admin/attendance/trainees').then(r => handleResponse<TraineeProfile[]>(r));

export const getPendingTrainees = () =>
  authenticatedFetch('/api/admin/attendance/trainees/pending').then(r => handleResponse<TraineeProfile[]>(r));

export const backfillAttendanceProfiles = () =>
  authenticatedFetch('/api/admin/attendance/trainees/backfill-profiles', { method: 'POST' })
    .then(r => handleResponse<{ targetUsers: number; createdProfiles: number; existingProfiles: number; message: string }>(r));

export const approveTrainee = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/trainees/approve?username=${encodeURIComponent(username)}`, { method: 'POST' })
    .then(r => handleResponse<TraineeProfile>(r));

export const getTrainee = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/trainees/${encodeURIComponent(username)}`).then(r => handleResponse<TraineeProfile>(r));

export const updateTrainee = (username: string, data: Partial<TraineeProfile>) =>
  authenticatedFetch(`/api/admin/attendance/trainees/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(r => handleResponse<TraineeProfile>(r));

export const deleteTrainee = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/trainees/${encodeURIComponent(username)}`, { method: 'DELETE' })
    .then(r => handleResponse<void>(r));

export const permanentlyDeleteTrainee = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/trainees/${encodeURIComponent(username)}/permanent`, { method: 'DELETE' })
    .then(r => handleResponse<void>(r));

export const resetTraineePassword = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/trainees/reset-password/${encodeURIComponent(username)}`, { method: 'POST' })
    .then(r => handleResponse<void>(r));

export const resetTraineeDevices = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/trainees/reset-devices/${encodeURIComponent(username)}`, { method: 'POST' })
    .then(r => handleResponse<void>(r));

export const grantEdit24h = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/user/${encodeURIComponent(username)}/grant-edit`, { method: 'POST' })
    .then(r => handleResponse<void>(r));

export const grantEdit24hForAll = () =>
  authenticatedFetch('/api/admin/attendance/allow-all-edit-24h', { method: 'POST' })
    .then(r => handleResponse<void>(r));

export const getPendingProfileEditRequests = () =>
  authenticatedFetch('/api/admin/attendance/profile-edit-requests').then(r => handleResponse<TraineeProfile[]>(r));

export const approveProfileEditRequest = (username: string, hours = 24) =>
  authenticatedFetch(`/api/admin/attendance/profile-edit-requests/${encodeURIComponent(username)}/approve?hours=${hours}`, {
    method: 'POST',
  }).then(r => handleResponse<TraineeProfile>(r));

export const rejectProfileEditRequest = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/profile-edit-requests/${encodeURIComponent(username)}/reject`, {
    method: 'POST',
  }).then(r => handleResponse<TraineeProfile>(r));

export const getCounsellorHierarchyMappings = (managerUsername?: string) => {
  const query = new URLSearchParams();
  if (managerUsername && managerUsername.trim()) {
    query.set('managerUsername', managerUsername.trim());
  }
  const suffix = query.toString() ? `?${query}` : '';
  return authenticatedFetch(`/api/admin/attendance/counsellor-hierarchy${suffix}`)
    .then(r => handleResponse<CounsellorHierarchyMapping[]>(r));
};

export const upsertCounsellorHierarchyMapping = (managerUsername: string, subordinateUsername: string) =>
  authenticatedFetch('/api/admin/attendance/counsellor-hierarchy', {
    method: 'POST',
    body: JSON.stringify({ managerUsername, subordinateUsername }),
  }).then(r => handleResponse<CounsellorHierarchyMapping>(r));

export const deleteCounsellorHierarchyMapping = (id: number) =>
  authenticatedFetch(`/api/admin/attendance/counsellor-hierarchy/${id}`, {
    method: 'DELETE',
  }).then(r => handleResponse<void>(r));

export const getSlots = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/slots/${encodeURIComponent(username)}`).then(r => handleResponse<AttendanceSlot[]>(r));

export const updateSlots = (username: string, slots: Omit<AttendanceSlot, 'id' | 'username'>[]) =>
  authenticatedFetch(`/api/admin/attendance/slots/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify({ slots }),
  }).then(r => handleResponse<AttendanceSlot[]>(r));

export const manualAttendanceUpdate = (username: string, data: ManualAttendanceRequest) =>
  authenticatedFetch(`/api/admin/attendance/attendance-manual/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(r => handleResponse<{ message: string; record: AttendanceRecord }>(r));

export const forcePunchOut = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/force-logout/${encodeURIComponent(username)}`, {
    method: 'POST',
  }).then(r => handleResponse<{ message: string; time: string; record: AttendanceRecord }>(r));

export const resolvePreviousPunchOut = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/resolve-previous-punch-out/${encodeURIComponent(username)}`, {
    method: 'POST',
  }).then(r => handleResponse<{ message: string; record: AttendanceRecord; resolvedDate: string }>(r));

export const getPendingLeaves = () =>
  authenticatedFetch('/api/admin/attendance/leaves').then(r => handleResponse<LeaveRequest[]>(r));

export const getAllLeaves = () =>
  authenticatedFetch('/api/admin/attendance/leaves/all').then(r => handleResponse<LeaveRequest[]>(r));

export const processLeave = (leaveId: number, action: 'APPROVE' | 'REJECT', adminReason?: string, newEndDate?: string) =>
  authenticatedFetch('/api/admin/attendance/leaves/process', {
    method: 'POST',
    body: JSON.stringify({ leaveId, action, adminReason, newEndDate }),
  }).then(r => handleResponse<LeaveRequest>(r));

export interface AdminLeaveUpdatePayload {
  startDate?: string;
  endDate?: string;
  reason?: string;
  adminReason?: string;
  halfDay?: boolean;
  leaveSession?: 'FIRST_HALF' | 'SECOND_HALF' | string;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
}

export const updateAdminLeave = (id: number, data: AdminLeaveUpdatePayload) =>
  authenticatedFetch(`/api/admin/attendance/leaves/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(r => handleResponse<LeaveRequest>(r));

export const deleteAdminLeave = (id: number) =>
  authenticatedFetch(`/api/admin/attendance/leaves/${id}`, {
    method: 'DELETE',
  }).then(r => handleResponse<void>(r));

export const getAdminEarlyLeaveLogs = (params: { month?: string; search?: string; status?: string; branch?: string }) => {
  const query = new URLSearchParams();
  if (params.month) query.set('month', params.month);
  if (params.search) query.set('search', params.search);
  if (params.status) query.set('status', params.status);
  if (params.branch) query.set('branch', params.branch);
  const suffix = query.toString() ? `?${query}` : '';
  return authenticatedFetch(`/api/admin/attendance/early-leaves${suffix}`).then(r => handleResponse<EarlyLeaveRequest[]>(r));
};

export const processEarlyLeave = (requestId: number, action: 'APPROVE' | 'REJECT', adminReason?: string) =>
  authenticatedFetch('/api/admin/attendance/early-leaves/process', {
    method: 'POST',
    body: JSON.stringify({ requestId, action, adminReason }),
  }).then(r => handleResponse<EarlyLeaveRequest>(r));

export const createAdminEarlyLeave = (data: Partial<EarlyLeaveRequest> & { username: string }) =>
  authenticatedFetch('/api/admin/attendance/early-leaves/direct', {
    method: 'POST',
    body: JSON.stringify(data),
  }).then(r => handleResponse<EarlyLeaveRequest>(r));

export const getAdminEarlyLeaveSlots = (username: string, date: string) =>
  authenticatedFetch(`/api/admin/attendance/early-leaves/slots?username=${encodeURIComponent(username)}&date=${encodeURIComponent(date)}`)
    .then(r => handleResponse<AttendanceSlot[]>(r));

export const directLeave = (username: string, data: Partial<LeaveRequest>) =>
  authenticatedFetch(`/api/admin/attendance/leaves/direct?username=${encodeURIComponent(username)}`, {
    method: 'POST',
    body: JSON.stringify(data),
  }).then(r => handleResponse<void>(r));

export const setLeaveQuota = (username: string, total: number) =>
  authenticatedFetch(`/api/admin/attendance/leaves/${encodeURIComponent(username)}/quota?total=${total}`, { method: 'PUT' })
    .then(r => handleResponse<{ message: string; username: string; appliedQuota: number }>(r));

export const setLeaveQuotaForAll = (total: number) =>
  authenticatedFetch(`/api/admin/attendance/leaves/quota/all?total=${total}`, { method: 'PUT' })
    .then(r => handleResponse<{ updatedUsers: number; appliedQuota: number }>(r));

export const getHolidays = (from: string, to: string) =>
  authenticatedFetch(`/api/admin/attendance/holidays?from=${from}&to=${to}`).then(r => handleResponse<AttendanceHoliday[]>(r));

export const addHoliday = (data: Omit<AttendanceHoliday, 'id'>) =>
  authenticatedFetch('/api/admin/attendance/holidays', {
    method: 'POST',
    body: JSON.stringify(data),
  }).then(r => handleResponse<AttendanceHoliday>(r));

export const deleteHoliday = (id: number) =>
  authenticatedFetch(`/api/admin/attendance/holidays/${id}`, { method: 'DELETE' })
    .then(r => handleResponse<void>(r));

export const getDropdownOptions = () =>
  authenticatedFetch('/api/attendance/dropdown-options').then(r => handleResponse<AttendanceDropdownOption[]>(r));

export const addDropdownOption = (data: Omit<AttendanceDropdownOption, 'id'>) =>
  authenticatedFetch('/api/admin/attendance/dropdown-options', {
    method: 'POST',
    body: JSON.stringify(data),
  }).then(r => handleResponse<AttendanceDropdownOption>(r));

export const deleteDropdownOption = (id: number) =>
  authenticatedFetch(`/api/admin/attendance/dropdown-options/${id}`, { method: 'DELETE' })
    .then(r => handleResponse<void>(r));

export const getMemoRecipients = () =>
  authenticatedFetch('/api/admin/attendance/memos/recipients').then(r => handleResponse<MemoRecipient[]>(r));

export const sendMemo = (data: { recipientUsername: string; memoType?: string; subject?: string; message: string }) =>
  authenticatedFetch('/api/admin/attendance/memos', {
    method: 'POST',
    body: JSON.stringify(data),
  }).then(r => handleResponse<Memo>(r));

export const getSentMemos = () =>
  authenticatedFetch('/api/admin/attendance/memos/sent').then(r => handleResponse<any[]>(r)).then(normalizeMemos);

export const deleteMemo = (id: number) =>
  authenticatedFetch(`/api/admin/attendance/memos/${id}`, { method: 'DELETE' }).then(r => handleResponse<void>(r));

// ── Admin Extended Reports (Phase 2) ─────────────────────────────────────────

type ExtraClassAction = 'APPROVE' | 'REJECT';

export const getAdminExtraClassLogs = (params: { month?: string; search?: string; status?: string; branch?: string; classType?: 'EXTRA_CLASS' | 'OTHER_CENTER' }) => {
  const query = new URLSearchParams();
  if (params.month) query.set('month', params.month);
  if (params.search) query.set('search', params.search);
  if (params.status) query.set('status', params.status);
  if (params.branch) query.set('branch', params.branch);
  if (params.classType) query.set('classType', params.classType);
  const suffix = query.toString() ? `?${query}` : '';
  return authenticatedFetch(`/api/admin/attendance/extra-classes${suffix}`).then(r => handleResponse<any[]>(r));
};

export const processExtraClass = (id: number, action: ExtraClassAction, adminReason?: string) =>
  authenticatedFetch('/api/admin/attendance/extra-classes/process', {
    method: 'POST',
    body: JSON.stringify({ id, action, adminReason }),
  }).then(r => handleResponse<any>(r));

export const createAdminExtraClass = (data: any) =>
  authenticatedFetch('/api/admin/attendance/extra-classes', {
    method: 'POST',
    body: JSON.stringify(data),
  }).then(r => handleResponse<any>(r));

export const updateAdminExtraClass = (id: number, data: any) =>
  authenticatedFetch(`/api/admin/attendance/extra-classes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(r => handleResponse<any>(r));

export const exportExtraClassReport = async (params: { month?: string; search?: string; branch?: string; classType?: 'EXTRA_CLASS' | 'OTHER_CENTER' }) => {
  const token = getToken();
  const query = new URLSearchParams();
  if (params.month) query.set('month', params.month);
  if (params.search) query.set('search', params.search);
  if (params.branch) query.set('branch', params.branch);
  if (params.classType) query.set('classType', params.classType);
  const suffix = query.toString() ? `?${query}` : '';
  const res = await apiFetch(`/api/admin/attendance/reports/extra-classes/export${suffix}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to export extra class report');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `extra_classes_${params.month || 'report'}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

export const getAdminClassCancelledLogs = (params: { month?: string; search?: string; branch?: string }) => {
  const query = new URLSearchParams();
  if (params.month) query.set('month', params.month);
  if (params.search) query.set('search', params.search);
  if (params.branch) query.set('branch', params.branch);
  const suffix = query.toString() ? `?${query}` : '';
  return authenticatedFetch(`/api/admin/attendance/classes-cancelled${suffix}`).then(r => handleResponse<any[]>(r));
};

export const exportClassCancelledReport = async (params: { month?: string; search?: string; branch?: string }) => {
  const token = getToken();
  const query = new URLSearchParams();
  if (params.month) query.set('month', params.month);
  if (params.search) query.set('search', params.search);
  if (params.branch) query.set('branch', params.branch);
  const suffix = query.toString() ? `?${query}` : '';
  const res = await apiFetch(`/api/admin/attendance/reports/classes-cancelled/export${suffix}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to export class cancelled report');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `class_cancelled_${params.month || 'report'}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

export const getAdminBreakLogs = (params: { month?: string; search?: string; type?: string; branch?: string }) => {
  const query = new URLSearchParams();
  if (params.month) query.set('month', params.month);
  if (params.search) query.set('search', params.search);
  if (params.type) query.set('type', params.type);
  if (params.branch) query.set('branch', params.branch);
  const suffix = query.toString() ? `?${query}` : '';
  return authenticatedFetch(`/api/admin/attendance/reports/breaks${suffix}`).then(r => handleResponse<any[]>(r));
};

export const processBreakAdmin = (breakId: number, action: 'APPROVE' | 'REJECT' | 'REVOKE', adminReason?: string) =>
  authenticatedFetch('/api/admin/attendance/breaks/process', {
    method: 'POST',
    body: JSON.stringify({ breakId, action, adminReason }),
  }).then(r => handleResponse<BreakLog>(r));

export const updateAdminBreak = (id: number, data: AdminBreakUpdatePayload) =>
  authenticatedFetch(`/api/admin/attendance/breaks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(r => handleResponse<BreakLog>(r));

export const deleteAdminBreak = (id: number) =>
  authenticatedFetch(`/api/admin/attendance/breaks/${id}`, {
    method: 'DELETE',
  }).then(r => handleResponse<void>(r));

export const directAdminBreakOut = (username: string, data?: Partial<BreakRequestPayload> & { reason?: string }) =>
  authenticatedFetch(`/api/admin/attendance/breaks/direct-out?username=${encodeURIComponent(username)}`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  }).then(r => handleResponse<BreakLog>(r));

export const directAdminBreakIn = (username: string) =>
  authenticatedFetch(`/api/admin/attendance/breaks/direct-in?username=${encodeURIComponent(username)}`, {
    method: 'POST',
  }).then(r => handleResponse<BreakLog>(r));

export const createAdminClassCancelled = (username: string, data: Partial<ClassCancelledLog>) =>
  authenticatedFetch(`/api/admin/attendance/classes-cancelled?username=${encodeURIComponent(username)}`, {
    method: 'POST',
    body: JSON.stringify(data),
  }).then(r => handleResponse<ClassCancelledLog>(r));

export const updateAdminClassCancelled = (id: number, data: Partial<ClassCancelledLog>) =>
  authenticatedFetch(`/api/admin/attendance/classes-cancelled/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(r => handleResponse<ClassCancelledLog>(r));

export const changeMyPassword = (currentPassword: string, newPassword: string, confirmPassword: string) =>
  authenticatedFetch('/login/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
  }).then(r => handleResponse<void>(r));

export const exportBreakReport = async (params: { month?: string; search?: string; type?: string; branch?: string }) => {
  const token = getToken();
  const query = new URLSearchParams();
  if (params.month) query.set('month', params.month);
  if (params.search) query.set('search', params.search);
  if (params.type) query.set('type', params.type);
  if (params.branch) query.set('branch', params.branch);
  const suffix = query.toString() ? `?${query}` : '';
  const res = await apiFetch(`/api/admin/attendance/reports/breaks/export${suffix}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to export break report');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const prefix = params.type === 'COLLEGE_VISIT' ? 'college_visit_report' : 'break_report';
  a.download = `${prefix}_${params.month || 'report'}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};
